import { copy as sharedUiCopy } from '@shared/copy';
import { Skeleton, SkeletonHead } from '@shared/ui/Skeleton';

/**
 * The pending state of `/content/:contentItemId/assets`.
 *
 * The surface is three blocks and the placeholder is the same three: the shape
 * card — a heading over a two-track fact list and the title-subtitle control —
 * then the asset panes, each of which is a heading with its badge, a lede, and
 * the drop zone or the row that stands under it.
 *
 * TWO panes, because two is the count a film gets — the trailer and the feature
 * — and a series replaces the second with its season list rather than adding a
 * third. The shape is unknown while the item read is in flight, so the
 * placeholder draws the count both shapes share.
 *
 * The head IS drawn here, unlike the sibling surfaces: this branch runs before
 * `item` resolves, and the breadcrumb's third crumb is the title's own name, so
 * the real head cannot render either.
 */
function AssetPaneSkeleton() {
  return (
    <section aria-hidden="true" className="mb-8 grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-7 w-[8rem]" />
        <Skeleton className="h-6 w-[5rem] rounded-pill" />
      </div>

      <Skeleton className="h-3 w-2/3 max-w-measure" />

      {/* The drop zone, which is what an empty pane draws and what a filled one
          replaces with a row of the same order of height. */}
      <Skeleton className="h-[7rem] w-full rounded-md" />
    </section>
  );
}

export function ContentAssetsSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        {sharedUiCopy.state.loading}
      </p>

      <SkeletonHead />

      <section aria-hidden="true" className="mb-8 grid gap-3 rounded-md border border-border bg-surface p-4">
        <Skeleton className="h-7 w-[9rem]" />

        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
          {Array.from({ length: 2 }, (_, row) => (
            <div key={row} className="contents">
              <Skeleton className="h-4 w-[5rem]" />
              <Skeleton className={row === 1 ? 'h-4 w-1/3' : 'h-6 w-[6rem] rounded-pill'} />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-3 w-[10rem]" />
          <Skeleton className="h-[2.75rem] w-[7rem] rounded-sm" />
        </div>
      </section>

      <AssetPaneSkeleton />
      <AssetPaneSkeleton />
    </>
  );
}
